import type { ErrorCode } from "@bodemleven/course-kit/schema/api";
import type { Locale, Role } from "@bodemleven/course-kit/schema/core";
import type {
  Grade,
  HyphaColor,
  HyphaKind,
  IndicatorKey,
  IndicatorStatus,
  LandUse,
  ManagementType,
  MeasureCategory,
  MoistureState,
  Priority,
  SampleStatus,
  SoilType,
  AdviceStatus,
  ResearchStatus,
} from "../domain/schema.ts";

/** Copy shared by every workspace section: navigation, actions, enum labels, units. */
export interface CommonCopy {
  nav: {
    /** Header/account-menu link to the workspace. */
    workspace: string;
    /** Label of the sidebar `<nav>`. */
    sectionsLabel: string;
    overview: string;
    lands: string;
    samples: string;
    research: string;
    advice: string;
    /** Admins only: calibrations, runner keys and the AI pipeline. */
    lab: string;
    /** Admins only: who holds which role. */
    users: string;
    /** The signed-in user's role, shown in the side pane; learners have none. */
    roleBadge: Record<Exclude<Role, "learner">, string>;
  };
  actions: {
    save: string;
    saving: string;
    cancel: string;
    edit: string;
    delete: string;
    deleting: string;
    back: string;
    retry: string;
    close: string;
    add: string;
    remove: string;
    moveUp: string;
    moveDown: string;
    viewAll: string;
    search: string;
    clearFilters: string;
  };
  status: {
    loading: string;
    saved: string;
    deleted: string;
    loadError: string;
    notFound: string;
    empty: string;
    /** Count of results after filtering, e.g. "3 van 12". */
    filtered: (shown: number, total: number) => string;
  };
  /**
   * Toast or alert after a failed request, per API error code (`lib/api-errors.ts`);
   * NETWORK: no answer at all, UNKNOWN: not a request error. Never the server's text.
   */
  apiErrors: Record<ErrorCode | "NETWORK" | "UNKNOWN", string>;
  /** Inline alert after a 409 STALE: someone else saved this record since it was loaded. */
  stale: {
    title: string;
    body: string;
    reload: string;
    discardTitle: string;
    discardBody: string;
    discard: string;
  };
  /** Every editor and form: save bar, leave guard and error summary. */
  forms: {
    /** Accessible name of the save bar. */
    actionsLabel: string;
    dirty: string;
    clean: string;
    leaveTitle: string;
    leaveBody: string;
    leave: string;
    stay: string;
    errorsTitle: (count: number) => string;
    errorsBody: string;
  };
  filters: {
    all: string;
    status: string;
    land: string;
    sort: string;
    newest: string;
    oldest: string;
  };
  landUse: Record<LandUse, string>;
  soilType: Record<SoilType, string>;
  management: Record<ManagementType, string>;
  moisture: Record<MoistureState, string>;
  sampleStatus: Record<SampleStatus, string>;
  researchStatus: Record<ResearchStatus, string>;
  adviceStatus: Record<AdviceStatus, string>;
  grade: Record<Grade, string>;
  indicator: Record<IndicatorKey, string>;
  /** Axis labels where the full name does not fit (radar, bar charts). */
  indicatorShort: Record<IndicatorKey, string>;
  indicatorStatus: Record<IndicatorStatus, string>;
  hyphaKind: Record<HyphaKind, string>;
  hyphaColor: Record<HyphaColor, string>;
  measureCategory: Record<MeasureCategory, string>;
  priority: Record<Priority, string>;
  units: {
    ugPerG: string;
    perG: string;
    ratio: string;
    /** After a percentage of the organisms counted, e.g. "4% of the count". */
    share: string;
    cm: string;
    um: string;
    mPerG: string;
    score: string;
  };
  fields: {
    owner: string;
    land: string;
    sample: string;
    createdAt: string;
    updatedAt: string;
    score: string;
    fbRatio: string;
  };
  /** Locale order and punctuation for values combined with labels. */
  format: {
    /** "Label: value", e.g. "Eigenaar: Jan". */
    labelled: (label: string, value: string) => string;
    /** Parts of a meta line joined by " · "; empty parts are left out. */
    meta: (parts: readonly MetaPart[]) => string;
    /** The meta separator on its own, before a part styled apart from the first. */
    separator: string;
    /** F:B ratio readout after its short label, e.g. "S:B 0,85 : 1". */
    ratio: (ratio: string) => string;
    /** A sample by its code, e.g. "Monster M-0001". */
    sample: (code: string) => string;
    /** Area with its unit, e.g. "1,5 ha". */
    hectares: (hectares: string) => string;
    /** Soil-life score out of 100, e.g. "72/100". */
    score: (score: string) => string;
    /** Score with its grade, e.g. "72/100 (Goed)". */
    scoreGrade: (score: string, grade: string) => string;
    /** A name with a detail, e.g. a chart legend "Bruin (42%)". */
    parenthetical: (main: string, detail: string) => string;
    /** A formatted number with its unit, e.g. "12,3 µg/g"; ratios and scores have their own. */
    withUnit: (value: string, unit: MeasureUnit) => string;
  };
}

type MetaPart = string | null | undefined | false;

/** Units written after a number; the F:B ratio and the score out of 100 read differently. */
type MeasureUnit = Exclude<keyof CommonCopy["units"], "ratio" | "score">;

const nlUnits: CommonCopy["units"] = {
  ugPerG: "µg/g",
  perG: "per g",
  ratio: "S:B",
  share: "van de telling",
  cm: "cm",
  um: "µm",
  mPerG: "m/g",
  score: "/100",
};

const enUnits: CommonCopy["units"] = {
  ugPerG: "µg/g",
  perG: "per g",
  ratio: "F:B",
  share: "of the count",
  cm: "cm",
  um: "µm",
  mPerG: "m/g",
  score: "/100",
};

export const commonCopy: Record<Locale, CommonCopy> = {
  nl: {
    nav: {
      workspace: "Bodemonderzoek",
      sectionsLabel: "Onderdelen van bodemonderzoek",
      overview: "Overzicht",
      lands: "Gebieden",
      samples: "Monsters",
      research: "Onderzoeken",
      advice: "Adviezen",
      lab: "Lab",
      users: "Gebruikers",
      roleBadge: { analyst: "Analist", admin: "Beheerder", owner: "Site-eigenaar" },
    },
    actions: {
      save: "Opslaan",
      saving: "Bezig met opslaan…",
      cancel: "Annuleren",
      edit: "Bewerken",
      delete: "Verwijderen",
      deleting: "Bezig met verwijderen…",
      back: "Terug",
      retry: "Opnieuw proberen",
      close: "Sluiten",
      add: "Toevoegen",
      remove: "Weghalen",
      moveUp: "Omhoog",
      moveDown: "Omlaag",
      viewAll: "Alles bekijken",
      search: "Zoeken",
      clearFilters: "Filters wissen",
    },
    status: {
      loading: "Bezig met laden…",
      saved: "Opgeslagen",
      deleted: "Verwijderd",
      loadError: "Dit kon niet worden geladen.",
      notFound: "Dit bestaat niet (meer) of je hebt er geen toegang toe.",
      empty: "Nog niets te zien.",
      filtered: (shown, total) => `${shown} van ${total}`,
    },
    apiErrors: {
      VALIDATION: "Niet alles is goed ingevuld. Controleer de gegevens en probeer het opnieuw.",
      UNAUTHORIZED: "Je bent niet meer ingelogd. Log opnieuw in en probeer het nog eens.",
      FORBIDDEN: "Je hebt geen toestemming om dit te doen.",
      NOT_FOUND: "Dit bestaat niet (meer) of je hebt er geen toegang toe.",
      CONFLICT: "Dit kan nu niet. Vernieuw de pagina en probeer het opnieuw.",
      STALE:
        "Iemand anders heeft dit intussen gewijzigd. Laad de nieuwste versie en probeer het opnieuw.",
      TOO_LARGE: "Dit is te groot om te versturen. Maak het kleiner en probeer het opnieuw.",
      RATE_LIMITED: "Te veel verzoeken achter elkaar. Wacht even en probeer het opnieuw.",
      INTERNAL: "Er ging iets mis op de server. Probeer het later opnieuw.",
      NETWORK:
        "De server is niet bereikbaar. Controleer je internetverbinding en probeer het opnieuw.",
      UNKNOWN: "Er ging iets mis. Probeer het opnieuw.",
    },
    stale: {
      title: "Iemand anders heeft dit intussen gewijzigd",
      body:
        "Je wijzigingen zijn niet opgeslagen. Laad de nieuwste versie en voer je wijzigingen " +
        "daarna opnieuw in.",
      reload: "Opnieuw laden",
      discardTitle: "Nieuwste versie laden?",
      discardBody: "Je wijzigingen die nog niet zijn opgeslagen, gaan dan verloren.",
      discard: "Wijzigingen weggooien en laden",
    },
    forms: {
      actionsLabel: "Opslaan",
      dirty: "Niet opgeslagen wijzigingen",
      clean: "Alles is opgeslagen",
      leaveTitle: "Wijzigingen niet opgeslagen",
      leaveBody: "Als je deze pagina verlaat, gaan je wijzigingen verloren.",
      leave: "Pagina verlaten",
      stay: "Blijven",
      errorsTitle: (count) =>
        count === 1 ? "1 veld klopt nog niet" : `${count} velden kloppen nog niet`,
      errorsBody: "Kies een fout om naar het veld te gaan.",
    },
    filters: {
      all: "Alle",
      status: "Status",
      land: "Gebied",
      sort: "Sorteren",
      newest: "Nieuwste eerst",
      oldest: "Oudste eerst",
    },
    landUse: {
      arable: "Akkerbouw",
      vegetables: "Groenteteelt",
      grassland: "Grasland en weide",
      bulbs: "Bollenteelt",
      orchard: "Fruitteelt en boomgaard",
      berries: "Kleinfruit en struiken",
      vineyard: "Wijngaard",
      forest: "Bos en voedselbos",
    },
    soilType: {
      sand: "Zand",
      clay: "Klei",
      peat: "Veen",
      loam: "Leem",
      loess: "Löss",
    },
    management: {
      conventional: "Gangbaar",
      transition: "In omschakeling",
      organic: "Biologisch",
      regenerative: "Regeneratief",
    },
    moisture: {
      dry: "Droog",
      moist: "Vochtig",
      wet: "Nat",
    },
    sampleStatus: {
      draft: "Concept",
      sent: "Verstuurd",
      received: "Ontvangen in lab",
      analyzing: "In analyse",
      analyzed: "Geanalyseerd",
    },
    researchStatus: {
      draft: "Concept",
      completed: "Afgerond",
    },
    adviceStatus: {
      draft: "Concept",
      published: "Gepubliceerd",
    },
    grade: {
      good: "Gezond",
      fair: "Redelijk",
      moderate: "Matig",
      poor: "Zwak",
    },
    indicator: {
      bacteria: "Bacteriën",
      fungi: "Schimmels",
      fbRatio: "Schimmel-bacterieverhouding",
      flagellates: "Flagellaten",
      amoebae: "Amoeben",
      beneficialNematodes: "Nuttige aaltjes",
      ciliates: "Trilhaardiertjes",
      rootFeeders: "Worteletende aaltjes",
    },
    indicatorShort: {
      bacteria: "Bacteriën",
      fungi: "Schimmels",
      fbRatio: "S:B",
      flagellates: "Flagellaten",
      amoebae: "Amoeben",
      beneficialNematodes: "Nuttige aaltjes",
      ciliates: "Trilhaardiertjes",
      rootFeeders: "Worteleters",
    },
    indicatorStatus: {
      low: "Te laag",
      ok: "Goed",
      high: "Te hoog",
      missing: "Niet gemeten",
      unscored: "Niet beoordeeld",
    },
    hyphaKind: {
      fungal: "Nuttige schimmel",
      oomycete: "Oömyceet",
      actinomycete: "Actinomyceet",
    },
    hyphaColor: {
      clear: "Helder",
      yellow: "Geel",
      orange: "Oranje",
      red: "Rood",
      green: "Groen",
      purple: "Paars",
      tan: "Lichtbruin",
      brown: "Bruin",
      dark_brown: "Donkerbruin",
    },
    measureCategory: {
      compost: "Compost",
      compost_tea: "Compostthee en -extract",
      cover_crop: "Groenbemesters en wortels",
      tillage: "Grondbewerking",
      water_air: "Lucht en water",
      organic_matter: "Organische stof en mulch",
      inputs: "Kunstmest en middelen",
      grazing: "Begrazing",
      monitoring: "Opvolging",
    },
    priority: {
      high: "Hoge prioriteit",
      medium: "Gemiddelde prioriteit",
      low: "Lage prioriteit",
    },
    units: nlUnits,
    fields: {
      owner: "Eigenaar",
      land: "Gebied",
      sample: "Monster",
      createdAt: "Aangemaakt",
      updatedAt: "Bijgewerkt",
      score: "Bodemlevenscore",
      fbRatio: "S:B-verhouding",
    },
    format: {
      labelled: (label, value) => `${label}: ${value}`,
      meta: (parts) => parts.filter(Boolean).join(" · "),
      separator: " · ",
      ratio: (ratio) => `S:B ${ratio}`,
      sample: (code) => `Monster ${code}`,
      hectares: (hectares) => `${hectares} ha`,
      score: (score) => `${score}/100`,
      scoreGrade: (score, grade) => `${score}/100 (${grade})`,
      parenthetical: (main, detail) => `${main} (${detail})`,
      withUnit: (value, unit) => `${value} ${nlUnits[unit]}`,
    },
  },
  en: {
    nav: {
      workspace: "Soil research",
      sectionsLabel: "Soil research sections",
      overview: "Overview",
      lands: "Areas",
      samples: "Samples",
      research: "Analyses",
      advice: "Advice",
      lab: "Lab",
      users: "Users",
      roleBadge: { analyst: "Analyst", admin: "Admin", owner: "Site owner" },
    },
    actions: {
      save: "Save",
      saving: "Saving…",
      cancel: "Cancel",
      edit: "Edit",
      delete: "Delete",
      deleting: "Deleting…",
      back: "Back",
      retry: "Try again",
      close: "Close",
      add: "Add",
      remove: "Remove",
      moveUp: "Move up",
      moveDown: "Move down",
      viewAll: "View all",
      search: "Search",
      clearFilters: "Clear filters",
    },
    status: {
      loading: "Loading…",
      saved: "Saved",
      deleted: "Deleted",
      loadError: "This could not be loaded.",
      notFound: "This does not exist (any more) or you have no access to it.",
      empty: "Nothing to show yet.",
      filtered: (shown, total) => `${shown} of ${total}`,
    },
    apiErrors: {
      VALIDATION: "Some details are not filled in correctly. Check them and try again.",
      UNAUTHORIZED: "You are no longer signed in. Sign in again and retry.",
      FORBIDDEN: "You are not allowed to do this.",
      NOT_FOUND: "This does not exist (any more) or you have no access to it.",
      CONFLICT: "This is not possible right now. Refresh the page and try again.",
      STALE: "Someone else changed this in the meantime. Load the latest version and try again.",
      TOO_LARGE: "This is too large to send. Make it smaller and try again.",
      RATE_LIMITED: "Too many requests in a row. Wait a moment and try again.",
      INTERNAL: "Something went wrong on the server. Please try again later.",
      NETWORK: "The server cannot be reached. Check your internet connection and try again.",
      UNKNOWN: "Something went wrong. Please try again.",
    },
    stale: {
      title: "Someone else changed this in the meantime",
      body: "Your changes have not been saved. Load the latest version, then make your changes again.",
      reload: "Reload",
      discardTitle: "Load the latest version?",
      discardBody: "Your unsaved changes will be lost.",
      discard: "Discard changes and reload",
    },
    forms: {
      actionsLabel: "Save",
      dirty: "Unsaved changes",
      clean: "Everything is saved",
      leaveTitle: "Unsaved changes",
      leaveBody: "If you leave this page, your changes are lost.",
      leave: "Leave page",
      stay: "Stay",
      errorsTitle: (count) =>
        count === 1 ? "1 field needs attention" : `${count} fields need attention`,
      errorsBody: "Choose an error to go to the field.",
    },
    filters: {
      all: "All",
      status: "Status",
      land: "Area",
      sort: "Sort",
      newest: "Newest first",
      oldest: "Oldest first",
    },
    landUse: {
      arable: "Arable farming",
      vegetables: "Vegetable growing",
      grassland: "Grassland and pasture",
      bulbs: "Bulb growing",
      orchard: "Fruit and orchard",
      berries: "Soft fruit and shrubs",
      vineyard: "Vineyard",
      forest: "Forest and food forest",
    },
    soilType: {
      sand: "Sand",
      clay: "Clay",
      peat: "Peat",
      loam: "Loam",
      loess: "Loess",
    },
    management: {
      conventional: "Conventional",
      transition: "In conversion",
      organic: "Organic",
      regenerative: "Regenerative",
    },
    moisture: {
      dry: "Dry",
      moist: "Moist",
      wet: "Wet",
    },
    sampleStatus: {
      draft: "Draft",
      sent: "Sent",
      received: "Received at lab",
      analyzing: "Being analysed",
      analyzed: "Analysed",
    },
    researchStatus: {
      draft: "Draft",
      completed: "Completed",
    },
    adviceStatus: {
      draft: "Draft",
      published: "Published",
    },
    grade: {
      good: "Healthy",
      fair: "Fair",
      moderate: "Moderate",
      poor: "Weak",
    },
    indicator: {
      bacteria: "Bacteria",
      fungi: "Fungi",
      fbRatio: "Fungal-to-bacterial ratio",
      flagellates: "Flagellates",
      amoebae: "Amoebae",
      beneficialNematodes: "Beneficial nematodes",
      ciliates: "Ciliates",
      rootFeeders: "Root-feeding nematodes",
    },
    indicatorShort: {
      bacteria: "Bacteria",
      fungi: "Fungi",
      fbRatio: "F:B",
      flagellates: "Flagellates",
      amoebae: "Amoebae",
      beneficialNematodes: "Good nematodes",
      ciliates: "Ciliates",
      rootFeeders: "Root feeders",
    },
    indicatorStatus: {
      low: "Too low",
      ok: "Good",
      high: "Too high",
      missing: "Not measured",
      unscored: "Not scored",
    },
    hyphaKind: {
      fungal: "Beneficial fungus",
      oomycete: "Oomycete",
      actinomycete: "Actinomycete",
    },
    hyphaColor: {
      clear: "Clear",
      yellow: "Yellow",
      orange: "Orange",
      red: "Red",
      green: "Green",
      purple: "Purple",
      tan: "Tan",
      brown: "Brown",
      dark_brown: "Dark brown",
    },
    measureCategory: {
      compost: "Compost",
      compost_tea: "Compost tea and extract",
      cover_crop: "Cover crops and roots",
      tillage: "Tillage",
      water_air: "Air and water",
      organic_matter: "Organic matter and mulch",
      inputs: "Fertiliser and chemicals",
      grazing: "Grazing",
      monitoring: "Follow-up",
    },
    priority: {
      high: "High priority",
      medium: "Medium priority",
      low: "Low priority",
    },
    units: enUnits,
    fields: {
      owner: "Owner",
      land: "Area",
      sample: "Sample",
      createdAt: "Created",
      updatedAt: "Updated",
      score: "Soil-life score",
      fbRatio: "F:B ratio",
    },
    format: {
      labelled: (label, value) => `${label}: ${value}`,
      meta: (parts) => parts.filter(Boolean).join(" · "),
      separator: " · ",
      ratio: (ratio) => `F:B ${ratio}`,
      sample: (code) => `Sample ${code}`,
      hectares: (hectares) => `${hectares} ha`,
      score: (score) => `${score}/100`,
      scoreGrade: (score, grade) => `${score}/100 (${grade})`,
      parenthetical: (main, detail) => `${main} (${detail})`,
      withUnit: (value, unit) => `${value} ${enUnits[unit]}`,
    },
  },
};
